"use client";

import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { cn } from "../../lib/utils";

// VALUE STYLING (large number)
const bigNumberValueVariants = cva(
  "min-w-0 overflow-hidden whitespace-nowrap font-bold tabular-nums leading-none tracking-tight",
  {
    variants: {
      size: {
        sm: "@sm:text-5xl @xs:text-4xl text-3xl",
        md: "@sm:text-7xl @xs:text-6xl text-4xl",
        lg: "@sm:text-8xl @xs:text-7xl text-5xl",
        xl: "@md:text-9xl @sm:text-8xl @xs:text-7xl text-6xl",
      },
      valueVariant: {
        default: "",
        gradient:
          "bg-gradient-to-br from-primary to-primary/60 bg-clip-text text-transparent",
        muted: "text-muted-foreground",
        primary: "text-primary",
      },
    },
    defaultVariants: {
      size: "lg",
      valueVariant: "default",
    },
  }
);

// LABEL STYLING (title)
const bigNumberLabelVariants = cva("font-medium tracking-tight", {
  variants: {
    size: {
      sm: "text-xs",
      md: "text-xs",
      lg: "text-sm",
      xl: "text-base",
    },
    labelStyle: {
      simple: "",
      bold: "font-semibold",
      uppercase: "uppercase tracking-wide",
      muted: "text-muted-foreground",
    },
  },
  defaultVariants: {
    size: "lg",
    labelStyle: "muted",
  },
});

// CONTAINER STYLING
const bigNumberContainerVariants = cva(
  "@container group relative flex min-w-0 flex-col transition-all duration-200",
  {
    variants: {
      variant: {
        card: "rounded-2xl bg-card p-6 text-card-foreground ring-1 ring-foreground/10",
        outline: ["rounded-lg border-2 p-6", "hover:border-primary/50"],
        ghost: ["rounded-lg p-6 hover:bg-muted/50"],
        minimal: "p-4",
      },
      alignment: {
        left: "items-start text-left",
        center: "items-center text-center",
        right: "items-end text-right",
      },
      clickable: {
        true: "cursor-pointer",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "card",
        clickable: true,
        className: "hover:ring-foreground/20",
      },
    ],
    defaultVariants: {
      variant: "card",
      alignment: "left",
      clickable: false,
    },
  }
);

// DESCRIPTION STYLING
const bigNumberDescriptionVariants = cva("leading-relaxed", {
  variants: {
    size: {
      sm: "text-xs",
      md: "text-sm",
      lg: "text-base",
      xl: "text-lg",
    },
  },
  defaultVariants: {
    size: "lg",
  },
});

export interface BigNumberItem {
  alignment?: "left" | "center" | "right";
  ariaLabel?: string;
  classNames?: {
    root?: string;
    header?: string;
    icon?: string;
    value?: string;
    label?: string;
    description?: string;
    footer?: string;
  };
  description?: string;
  footer?: React.ReactNode;
  header?: React.ReactNode;
  href?: string;
  icon?: React.ReactNode;
  id?: string | number;
  label?: string;
  onClick?: () => void;
  prefix?: string;
  size?: "sm" | "md" | "lg" | "xl";
  suffix?: string;
  value: string | number;
  valueVariant?: "default" | "gradient" | "muted" | "primary";
  variant?: "card" | "outline" | "ghost" | "minimal";
}

export interface BigNumberItemProps
  extends Pick<
    VariantProps<typeof bigNumberValueVariants>,
    "size" | "valueVariant"
  > {
  alignment?: "left" | "center" | "right";
  as?: "div" | "article";
  classNames?: {
    root?: string;
    header?: string;
    icon?: string;
    value?: string;
    label?: string;
    description?: string;
    footer?: string;
  };
  descriptionPosition?: "above" | "below";
  item: BigNumberItem;
  labelPosition?: "above" | "below";
  labelStyle?: "simple" | "bold" | "uppercase" | "muted";
  onClick?: () => void;
  renderIcon?: (icon: React.ReactNode, item: BigNumberItem) => React.ReactNode;
  renderLabel?: (label: string, item: BigNumberItem) => React.ReactNode;
  renderValue?: (item: BigNumberItem) => React.ReactNode;
  showIcon?: boolean;
  variant?: "card" | "outline" | "ghost" | "minimal";
}

function buildBigNumberContent(
  item: BigNumberItem,
  renderIconContent: () => React.ReactNode,
  renderValueContent: () => React.ReactNode,
  renderLabelContent: () => React.ReactNode,
  renderDescription: () => React.ReactNode,
  labelPosition: "above" | "below",
  descriptionPosition: "above" | "below",
  classNames?: BigNumberItemProps["classNames"]
) {
  return (
    <>
      {item.header && (
        <div className={cn("mb-3", classNames?.header)}>{item.header}</div>
      )}
      {renderIconContent()}
      <div className="flex w-full min-w-0 flex-col gap-1.5">
        {labelPosition === "above" && renderLabelContent()}
        {descriptionPosition === "above" && renderDescription()}
        {renderValueContent()}
        {labelPosition === "below" && renderLabelContent()}
        {descriptionPosition === "below" && renderDescription()}
      </div>
      {item.footer && (
        <div className={cn("mt-3", classNames?.footer)}>{item.footer}</div>
      )}
    </>
  );
}

export function BigNumberItem({
  item,
  size = "lg",
  variant = "card",
  valueVariant = "default",
  alignment = "left",
  labelStyle = "muted",
  labelPosition = "above",
  descriptionPosition = "below",
  showIcon = true,
  onClick,
  as: Element = "div",
  renderValue,
  renderLabel,
  renderIcon,
  classNames,
}: BigNumberItemProps) {
  const finalSize = item.size ?? size;
  const finalVariant = item.variant ?? variant;
  const finalValueVariant = item.valueVariant ?? valueVariant;
  const finalAlignment = item.alignment ?? alignment;

  const isLink = Boolean(item.href);
  const isClickable = Boolean(onClick) || Boolean(item.onClick) || isLink;

  const handleClick = () => {
    if (item.onClick) {
      item.onClick();
    }
    if (onClick) {
      onClick();
    }
  };

  const renderValueContent = () => {
    if (renderValue) {
      return renderValue(item);
    }

    const valueText = `${item.prefix ?? ""}${item.value}${item.suffix ?? ""}`;

    return (
      <div
        className={cn(
          bigNumberValueVariants({
            size: finalSize,
            valueVariant: finalValueVariant,
          }),
          classNames?.value
        )}
      >
        {valueText}
      </div>
    );
  };

  const renderLabelContent = () => {
    if (!item.label) {
      return null;
    }

    if (renderLabel) {
      return renderLabel(item.label, item);
    }

    return (
      <div
        className={cn(
          bigNumberLabelVariants({ size: finalSize, labelStyle }),
          classNames?.label
        )}
      >
        {item.label}
      </div>
    );
  };

  const renderIconContent = () => {
    if (!(showIcon && item.icon)) {
      return null;
    }

    if (renderIcon) {
      return renderIcon(item.icon, item);
    }

    return (
      <div
        className={cn(
          "mb-3 flex items-center justify-center rounded-md bg-muted/50 p-2",
          finalSize === "sm" && "h-8 w-8",
          finalSize === "md" && "h-10 w-10",
          finalSize === "lg" && "h-12 w-12",
          finalSize === "xl" && "h-14 w-14",
          classNames?.icon
        )}
      >
        {item.icon}
      </div>
    );
  };

  const renderDescriptionContent = () => {
    if (!item.description) {
      return null;
    }

    return (
      <div
        className={cn(
          bigNumberDescriptionVariants({ size: finalSize }),
          "text-muted-foreground",
          classNames?.description
        )}
      >
        {item.description}
      </div>
    );
  };

  const content = buildBigNumberContent(
    item,
    renderIconContent,
    renderValueContent,
    renderLabelContent,
    renderDescriptionContent,
    labelPosition,
    descriptionPosition,
    classNames
  );

  const containerClassName = cn(
    bigNumberContainerVariants({
      variant: finalVariant,
      alignment: finalAlignment,
      clickable: isClickable,
    }),
    classNames?.root
  );

  const ariaLabel =
    item.ariaLabel ?? (item.label ? `${item.label}: ${item.value}` : undefined);

  if (isLink && item.href) {
    return (
      <a
        aria-label={ariaLabel}
        className={containerClassName}
        href={item.href}
        onClick={handleClick}
      >
        {content}
      </a>
    );
  }

  if (isClickable) {
    return (
      <button
        aria-label={ariaLabel}
        className={containerClassName}
        onClick={handleClick}
        type="button"
      >
        {content}
      </button>
    );
  }

  return (
    <Element aria-label={ariaLabel} className={containerClassName}>
      {content}
    </Element>
  );
}
